import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, CalendarOff, Loader2, Sparkles } from "lucide-react";
import type { AvailabilityResponse, Branch, TimeSlot } from "@autopic/shared";
import { Button } from "../../components/Button";
import { StepHeader } from "../../components/StepHeader";
import { api } from "../../api/client";
import { track } from "../../analytics/analytics";
import { useSession } from "../../store/sessionStore";
import { Calendar } from "./Calendar";
import { CallbackPanel } from "./CallbackPanel";
import { GarageCallLink } from "./GarageCallLink";
import { findNextAvailable, formatDayLabel } from "../dates";
import type { StepProps } from "../stepTypes";
import s from "../steps.module.css";

const BOOKING_WINDOW_DAYS = 366;

/** Step 6 — branch picker + calendar + time-slot pills, with graceful no-slots. */
export function DateTimeStep({ onNext, saveLead }: StepProps) {
  const config = useSession((st) => st.config)!;
  const garageId = useSession((st) => st.garageId)!;
  const branches = useSession((st) => st.branches);
  const branchId = useSession((st) => st.branchId);
  const setBranchId = useSession((st) => st.setBranchId);
  const selectedServiceIds = useSession((st) => st.selectedServiceIds);
  const vehicleRegistration = useSession((st) => st.vehicleRegistration);
  const slot = useSession((st) => st.slot);
  const setSlot = useSession((st) => st.setSlot);
  const setContactInfoFields = useSession((st) => st.setContactInfoFields);

  const allBranches = branches.length ? branches : config.branches;
  const selectedBranch = allBranches.find((branch) => branch.id === branchId);

  // Default-select the nearest/first branch.
  useEffect(() => {
    if (!branchId && allBranches.length) setBranchId(allBranches[0]!.id);
  }, [branchId, allBranches, setBranchId]);

  const [avail, setAvail] = useState<AvailabilityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const noSlotsTracked = useRef(false);

  // Fetch availability whenever branch or services change.
  useEffect(() => {
    if (!branchId || selectedServiceIds.length === 0) return;
    let alive = true;
    setLoading(true);
    setAvail(null);
    setContactInfoFields(null);
    setSelectedDate(null);
    api
      .getAvailability(garageId, {
        branchId,
        serviceIds: selectedServiceIds,
        vehicleRegistration: vehicleRegistration ?? undefined,
        regNoCountry: "GB",
        days: BOOKING_WINDOW_DAYS,
      })
      .then((res) => {
        if (!alive) return;
        setAvail(res);
        setContactInfoFields(res.contactInfoFields ?? null);
        const firstAvail = res.days.find((d) => d.hasAvailability);
        setSelectedDate(firstAvail?.date ?? res.days[0]?.date ?? null);
      })
      .catch(() => {
        if (alive) setAvail(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [
    garageId,
    branchId,
    selectedServiceIds,
    vehicleRegistration,
    setContactInfoFields,
  ]);

  const dayDetail = useMemo(
    () => avail?.days.find((d) => d.date === selectedDate) ?? null,
    [avail, selectedDate],
  );
  const slotsForDay = (dayDetail?.slots ?? []).filter((sl) => sl.available);
  const dayHasNone = !!dayDetail && slotsForDay.length === 0;
  const nextAvail = avail
    ? findNextAvailable(avail.days, selectedDate ?? undefined)
    : null;

  // Fire no_slots analytics once per empty day view.
  useEffect(() => {
    if (dayHasNone && !noSlotsTracked.current) {
      noSlotsTracked.current = true;
      track("no_slots_shown", { branchId, date: selectedDate });
    }
    if (!dayHasNone) noSlotsTracked.current = false;
  }, [dayHasNone, branchId, selectedDate]);

  function pickSlot(sl: TimeSlot) {
    setSlot(sl);
    track("datetime_selected", { branchId, slotId: sl.id, start: sl.start });
  }

  function handleContinue() {
    if (!slot) return;
    saveLead("datetime");
    onNext();
  }

  function changeBranch(b: Branch) {
    setBranchId(b.id);
    setSlot(null);
  }

  return (
    <div>
      <StepHeader
        kicker="Date & branch"
        title={config.copy.datetimeTitle}
        subtitle="Choose where and when — availability is live."
        display={false}
      />

      <div className={s.stack}>
        {/* Branch picker */}
        {config.sandbox && allBranches.length > 1 && (
          <div>
            <p className={s.slotLabel}>Choose a branch</p>
            <div className={s.branchList} role="radiogroup" aria-label="Branch">
              {allBranches.map((b) => {
                const sel = b.id === branchId;
                return (
                  <button
                    key={b.id}
                    type="button"
                    role="radio"
                    aria-checked={sel}
                    className={[s.branchItem, sel ? s.branchSelected : ""].join(
                      " ",
                    )}
                    onClick={() => changeBranch(b)}
                  >
                    <span className={s.branchRadio} aria-hidden="true" />
                    <span className={s.branchBody}>
                      <span className={s.branchName}>{b.name}</span>
                      <span className={s.branchAddr}>
                        {b.addressLine}, {b.postcode}
                      </span>
                    </span>
                    {typeof b.distanceMiles === "number" && (
                      <span className={s.branchDist}>{b.distanceMiles} mi</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Calendar */}
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <Loader2
              size={26}
              style={{
                animation: "ap-spin 0.9s linear infinite",
                color: "var(--ap-primary)",
              }}
            />
          </div>
        ) : avail ? (
          <>
            <Calendar
              days={avail.days}
              selected={selectedDate}
              onSelect={(iso) => {
                setSelectedDate(iso);
                setSlot(null);
              }}
            />

            {/* Slots or graceful no-slots */}
            {dayHasNone ? (
              <div className={s.noSlots}>
                <p className={s.noSlotsTitle}>
                  <CalendarOff size={17} /> No online slots on{" "}
                  {selectedDate ? formatDayLabel(selectedDate) : "this day"}
                </p>
                <p className={s.noSlotsText}>{config.copy.noSlotsMessage}</p>
                {nextAvail && (
                  <button
                    type="button"
                    className={s.nextAvail}
                    onClick={() => {
                      setSelectedDate(nextAvail.date);
                      setSlot(null);
                    }}
                  >
                    <Sparkles size={15} /> Next available:{" "}
                    {formatDayLabel(nextAvail.date)} →
                  </button>
                )}
                <GarageCallLink
                  phone={selectedBranch?.phone}
                  locationName={selectedBranch?.name}
                  reason="Need help finding a time?"
                />
                <CallbackPanel saveLead={saveLead} />
              </div>
            ) : (
              <div className={s.slotSection}>
                <p className={s.slotLabel}>
                  Appointment time{" "}
                  {selectedDate ? `· ${formatDayLabel(selectedDate)}` : ""}
                </p>
                <div
                  className={s.slotGrid}
                  role="group"
                  aria-label="Time slots"
                >
                  {slotsForDay.map((sl) => {
                    const sel = slot?.id === sl.id;
                    return (
                      <button
                        key={sl.id}
                        type="button"
                        className={[s.slot, sel ? s.slotSelected : ""].join(
                          " ",
                        )}
                        aria-pressed={sel}
                        onClick={() => pickSlot(sl)}
                      >
                        {sl.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={s.noSlots}>
            <p className={s.noSlotsTitle}>
              <CalendarOff size={17} /> Availability unavailable
            </p>
            <p className={s.noSlotsText}>{config.copy.noSlotsMessage}</p>
            <GarageCallLink
              phone={selectedBranch?.phone}
              locationName={selectedBranch?.name}
              reason="Prefer to speak to the team?"
            />
            <CallbackPanel saveLead={saveLead} />
          </div>
        )}
      </div>

      <div className={s.actions}>
        <Button
          block
          disabled={!slot}
          onClick={handleContinue}
          trailingIcon={<ArrowRight size={18} />}
        >
          {config.copy.ctaContinue}
        </Button>
      </div>
    </div>
  );
}
